import type { SortDirection } from '#layers/base/public';
import type { FilterRelationalQuantifier, LogicalOperator, UnaryFilterOperator } from '#layers/data-model/types/filters';
import type {
	GlobalCollectionId,
	GlobalPrimitiveFieldId,
	GlobalSingleRelationFieldId,
} from '#layers/data-model/types/schema/ids';
import type {
	InputField,
	InputObjectType,
	MonospaceCollection,
	Operation,
	OperationKind,
	OutputField,
	OutputObjectType,
	QuerySchema,
	SchemaManifest,
	SchemaManifestInputObjectTypeRef,
	SchemaManifestTypeRef,
} from '#layers/data-model/types/schema/schema-manifest';
import { isSortDirection } from '#layers/base/public';
import { FILTER_RELATIONAL_QUANTIFIERS } from '#layers/data-model/types/filters';
import { InputObjectTypeConstraints, QueryOperationKinds, SchemaManifestInputFieldKinds, SchemaManifestTypePools } from '#layers/data-model/types/schema/schema-manifest';

/** The engine's argument and nested-operation keys, as they appear on the wire. */
const WireKeys = {
	Data: 'data',
	Filter: 'filter',
	Sort: 'sort',
	Limit: 'limit',
	Offset: 'offset',
	Direction: 'direction',
	Meta: 'meta',
	TotalCount: 'totalCount',
	Create: '_create',
	Connect: '_connect',
	Disconnect: '_disconnect',
	Update: '_update',
	Delete: '_delete',
} as const;

const NO_ARGUMENTS: InputObjectType = { fields: [] };

/**
 * An opinionated walker over the schema manifest's `querySchema` graph: what
 * the /items API accepts, per collection. This is where Studio learns that a
 * collection has no `create` operation, which operators a field can be
 * filtered by, or that a relation offers `_connect` but not `_create`.
 *
 * The graph itself makes no assumptions — it is the API's full type algebra.
 * The walker assumes the canonical argument shapes: reads carry
 * `filter`/`sort`/`limit`/`offset`, writes carry `data`, relation inputs are
 * operation objects keyed `_create`/`_connect`/`_disconnect`/`_update`/
 * `_delete`, to-many filter traversals wrap the target filter in
 * `_some`/`_every`/`_none`. Anything that does not match reads as absent —
 * a missing affordance, never a broken one.
 *
 * Every accessor takes a global collection or field id. The walker resolves
 * each field within its current collection before walking the name-based wire
 * graph, including when a relation moves the walk to another collection.
 *
 * `collectionById()` returning `undefined` strictly means "the API schema exposes
 * no operations for this collection". Studio-only virtual fields, synthetic
 * filter collections, and collections outside the workspace manifest all read
 * as absent here; the fallback policy belongs to the caller.
 *
 * IDs from another collection read as absent, even when that collection has a
 * field with the same API name.
 */
export class QuerySchemaWalker {
	private readonly pools: Pools;

	constructor(manifest: SchemaManifest) {
		this.pools = new Pools(manifest);
	}

	/** The API surface of a collection, or `undefined` when it exposes no operations. */
	collectionById(id: GlobalCollectionId): QuerySchemaCollection | undefined {
		const collection = this.pools.collection(id);
		const apiName = collection && this.pools.strings[collection.apiName];
		if (apiName === undefined) return undefined;
		const operations = this.pools.graph.operations.filter(
			(operation) => this.pools.strings[operation[1]] === apiName,
		);

		return operations.length > 0 ? new QuerySchemaCollection(this.pools, id, operations) : undefined;
	}
}

export class QuerySchemaCollection {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly ops: Operation[],
	) {}

	/** The arguments of a filtered list read. */
	readMany(): QuerySchemaRead | undefined {
		const operation = this.op(QueryOperationKinds.ReadMany);
		const args = operation && this.pools.objectAt(operation[2]);
		return args && new QuerySchemaRead(this.pools, this.collectionId, args, operation[3]);
	}

	/** Whether single rows are addressable by key (`GET /items/{c}/{id}`). */
	readOne(): QuerySchemaReadOne | undefined {
		const operation = this.op(QueryOperationKinds.ReadOne);
		return operation && new QuerySchemaReadOne(this.pools, this.collectionId, operation[3]);
	}

	create(): QuerySchemaWrite | undefined {
		return this.write(QueryOperationKinds.Create);
	}

	/** The `data` surface of a filtered bulk update. */
	updateMany(): QuerySchemaWrite | undefined {
		return this.write(QueryOperationKinds.UpdateMany);
	}

	updateOne(): QuerySchemaWrite | undefined {
		return this.write(QueryOperationKinds.UpdateOne);
	}

	/** Whether rows can be deleted by filter. */
	deleteMany(): QuerySchemaDelete | undefined {
		const operation = this.op(QueryOperationKinds.DeleteMany);
		const args = operation && this.pools.objectAt(operation[2]);
		return args && new QuerySchemaDelete(this.pools, this.collectionId, args);
	}

	deleteOne(): boolean {
		return this.op(QueryOperationKinds.DeleteOne) !== undefined;
	}

	private op(kind: OperationKind): Operation | undefined {
		return this.ops.find((operation) => operation[0] === kind);
	}

	private write(kind: OperationKind): QuerySchemaWrite | undefined {
		const operation = this.op(kind);
		const args = operation && this.pools.objectAt(operation[2]);
		const data = args && this.pools.field(args, WireKeys.Data);
		const object = data && this.pools.objectOf(data.type);
		return object && new QuerySchemaWrite(this.pools, this.collectionId, object);
	}
}

/** The output of the by-key read, independently of the list read's output. */
export class QuerySchemaReadOne {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly outputType: SchemaManifestTypeRef,
	) {}

	output(): QuerySchemaOutput | undefined {
		const object = this.pools.outputObjectOf(this.outputType);
		return object && new QuerySchemaOutput(this.pools, this.collectionId, object);
	}
}

/** The filter accepted by a bulk delete. Its response is not a read selection. */
export class QuerySchemaDelete {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly args: InputObjectType,
	) {}

	filter(): QuerySchemaFilter | undefined {
		const field = this.pools.field(this.args, WireKeys.Filter);
		const object = field && this.pools.objectOf(field.type);
		return object && new QuerySchemaFilter(this.pools, this.collectionId, object);
	}
}

export class QuerySchemaRead {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly args: InputObjectType,
		private readonly outputType: SchemaManifestTypeRef,
	) {}

	/** The fields each returned row carries. */
	output(): QuerySchemaOutput | undefined {
		const object = this.pools.outputObjectOf(this.outputType);
		return object && new QuerySchemaOutput(this.pools, this.collectionId, object);
	}

	filter(): QuerySchemaFilter | undefined {
		const field = this.pools.field(this.args, WireKeys.Filter);
		const object = field && this.pools.objectOf(field.type);
		return object && new QuerySchemaFilter(this.pools, this.collectionId, object);
	}

	sorting(): QuerySchemaSort | undefined {
		const field = this.pools.field(this.args, WireKeys.Sort);
		const object = field && this.pools.objectOf(field.type);
		return object && new QuerySchemaSort(this.pools, this.collectionId, object);
	}

	/** Whether reads accept `limit`, which caps the rows returned without paging through the rest. */
	limit(): boolean {
		return this.pools.field(this.args, WireKeys.Limit) !== undefined;
	}

	/** Whether reads accept `limit`/`offset`. */
	pagination(): boolean {
		return this.pools.field(this.args, WireKeys.Limit) !== undefined
			&& this.pools.field(this.args, WireKeys.Offset) !== undefined;
	}

	/** Whether reads accept `meta.totalCount`. */
	totalCount(): boolean {
		const meta = this.pools.field(this.args, WireKeys.Meta);
		const object = meta && this.pools.objectOf(meta.type);
		return !!object && this.pools.field(object, WireKeys.TotalCount) !== undefined;
	}
}

/** The fields an operation returns, keyed like the collection schema. */
export class QuerySchemaOutput {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly object: OutputObjectType,
	) {}

	primitiveFieldById(id: GlobalPrimitiveFieldId): boolean {
		const apiName = this.pools.primitiveName(this.collectionId, id);
		return apiName !== undefined && this.pools.outputField(this.object, apiName) !== undefined;
	}

	/** The target collection's returned fields behind a relation field. */
	relationFieldById(id: GlobalSingleRelationFieldId): QuerySchemaOutput | undefined {
		return this.relationReadById(id)?.output();
	}

	/**
	 * The read behind a returned relation field: the arguments its `include` accepts, and the target
	 * collection's returned fields. A relation field without arguments accepts none.
	 */
	relationReadById(id: GlobalSingleRelationFieldId): QuerySchemaRead | undefined {
		const relation = this.pools.relation(this.collectionId, id);
		const field = relation && this.pools.outputField(this.object, relation.apiName);
		if (!field) return undefined;
		const args = field.arguments === undefined ? NO_ARGUMENTS : this.pools.objectAt(field.arguments);
		return args && new QuerySchemaRead(this.pools, relation.oppositeCollectionId, args, field.type);
	}
}

export class QuerySchemaFilter {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly object: InputObjectType,
	) {}

	primitiveFieldById(id: GlobalPrimitiveFieldId): QuerySchemaFieldFilter | undefined {
		const apiName = this.pools.primitiveName(this.collectionId, id);
		if (apiName === undefined) return undefined;
		const field = this.pools.field(this.object, apiName);

		if (field?.kind !== SchemaManifestInputFieldKinds.Primitive) return undefined;

		const operators = this.pools.objectOf(field.type);
		return operators && new QuerySchemaFieldFilter(this.pools, operators);
	}

	/** The target collection's filter surface behind a relation traversal. */
	relationFieldById(id: GlobalSingleRelationFieldId): QuerySchemaFilter | undefined {
		const relation = this.pools.relation(this.collectionId, id);
		if (!relation) return undefined;
		const field = this.pools.field(this.object, relation.apiName);

		if (field?.kind !== SchemaManifestInputFieldKinds.Relation) return undefined;

		const target = this.pools.objectOf(field.type);

		if (!target) return undefined;

		// To-many traversals wrap the target filter in a quantifier object.
		const quantified = FILTER_RELATIONAL_QUANTIFIERS.map((quantifier) => this.pools.field(target, quantifier)).find(Boolean);
		const object = quantified ? this.pools.objectOf(quantified.type) : target;

		return object && new QuerySchemaFilter(this.pools, relation.oppositeCollectionId, object);
	}

	/** The quantifiers a to-many traversal accepts (`_some`, `_every`, `_none`); empty for to-one. */
	relationQuantifiersById(id: GlobalSingleRelationFieldId): FilterRelationalQuantifier[] {
		const relation = this.pools.relation(this.collectionId, id);
		const field = relation && this.pools.field(this.object, relation.apiName);
		const target = field?.kind === SchemaManifestInputFieldKinds.Relation ? this.pools.objectOf(field.type) : undefined;
		return target ? FILTER_RELATIONAL_QUANTIFIERS.filter((quantifier) => this.pools.field(target, quantifier) !== undefined) : [];
	}

	/** Whether the filter accepts a logical combinator (`_and`, `_or`, `_not`). */
	logical(operator: LogicalOperator | UnaryFilterOperator): boolean {
		return this.pools.field(this.object, operator) !== undefined;
	}

	/** Whether one filter object may hold several keys; without `and`, each term needs its own. */
	combinesKeys(): boolean {
		return this.pools.combinesFields(this.object);
	}
}

export class QuerySchemaFieldFilter {
	constructor(
		private readonly pools: Pools,
		private readonly object: InputObjectType,
	) {}

	/** The operator keys the field accepts (`_eq`, `_between`, …). */
	operators(): string[] {
		return this.pools.fieldNames(this.object);
	}

	/** Whether one operator object may hold several operators, such as `_gte` and `_lte`. */
	combinesOperators(): boolean {
		return this.pools.combinesFields(this.object);
	}
}

export class QuerySchemaSort {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly object: InputObjectType,
	) {}

	/** The directions a field sorts by; `undefined` when the field is not sortable. Unknown directions are dropped. */
	directionsById(id: GlobalPrimitiveFieldId): SortDirection[] | undefined {
		const apiName = this.pools.primitiveName(this.collectionId, id);
		const field = apiName === undefined ? undefined : this.pools.field(this.object, apiName);
		const entry = field && this.pools.objectOf(field.type);
		if (!entry) return undefined;
		const direction = this.pools.field(entry, WireKeys.Direction);
		return direction ? this.pools.enumValues(direction.type).filter((value) => isSortDirection(value)) : [];
	}
}

export interface QuerySchemaWriteField {
	/** Whether the payload may omit the field. */
	optional: boolean;
	/** Whether omitting it falls back to an engine- or database-side default. */
	hasDefault: boolean;
	/** Whether the payload may set it to `null`. */
	nullable: boolean;
}

/** The `data` surface of a create or update operation. */
export class QuerySchemaWrite {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly object: InputObjectType,
	) {}

	/** A field's write facts; `undefined` means the payload does not accept it. */
	primitiveFieldById(id: GlobalPrimitiveFieldId): QuerySchemaWriteField | undefined {
		const apiName = this.pools.primitiveName(this.collectionId, id);
		if (apiName === undefined) return undefined;
		const field = this.pools.field(this.object, apiName);

		if (field?.kind !== SchemaManifestInputFieldKinds.Primitive) return undefined;

		return {
			optional: field.optional ?? false,
			hasDefault: field.hasDefault ?? false,
			nullable: isNullableTypeRef(field.type),
		};
	}

	relationFieldById(id: GlobalSingleRelationFieldId): QuerySchemaRelationWrite | undefined {
		const relation = this.pools.relation(this.collectionId, id);
		if (!relation) return undefined;
		const field = this.pools.field(this.object, relation.apiName);

		if (field?.kind !== SchemaManifestInputFieldKinds.Relation) return undefined;

		const operations = this.pools.objectOf(field.type);
		return operations && new QuerySchemaRelationWrite(this.pools, relation.oppositeCollectionId, operations, field.optional ?? false);
	}
}

/** The nested operations a write payload offers on one relation field. */
export class QuerySchemaRelationWrite {
	constructor(
		private readonly pools: Pools,
		private readonly collectionId: GlobalCollectionId,
		private readonly ops: InputObjectType,
		/** Whether the payload may omit the relation. */
		readonly optional: boolean,
	) {}

	/**
	 * The write surface of inline creation through the relation, or
	 * `undefined` when `_create` is not offered. Nested surfaces are not the
	 * target collection's top-level ones — a keyless collection can be
	 * creatable through a parent while offering no top-level create.
	 */
	create(): QuerySchemaWrite | undefined {
		return this.nestedWrite(WireKeys.Create);
	}

	/** Whether existing rows can be connected. */
	connect(): boolean {
		return this.pools.field(this.ops, WireKeys.Connect) !== undefined;
	}

	disconnect(): boolean {
		return this.pools.field(this.ops, WireKeys.Disconnect) !== undefined;
	}

	/** The write surface of `_update` through the relation, if offered. */
	update(): QuerySchemaWrite | undefined {
		return this.nestedWrite(WireKeys.Update);
	}

	/** Whether related rows can be deleted through the relation. */
	delete(): boolean {
		return this.pools.field(this.ops, WireKeys.Delete) !== undefined;
	}

	private nestedWrite(key: string): QuerySchemaWrite | undefined {
		const field = this.pools.field(this.ops, key);
		const args = field && this.pools.objectOf(field.type);
		const data = args && this.pools.field(args, WireKeys.Data);
		const object = data && this.pools.objectOf(data.type);
		return object && new QuerySchemaWrite(this.pools, this.collectionId, object);
	}
}

/**
 * The manifest's interned pools with the lookups that dereference them — the
 * decode-side counterpart of the engine's `ManifestPools`, which interned
 * everything on the way in. Graph lookups walk the manifest; the collection
 * index keeps field identity scoped through relation traversals.
 */
class Pools {
	readonly strings: string[];
	readonly graph: QuerySchema;
	private readonly collections: MonospaceCollection[];
	private readonly collectionsById: Map<GlobalCollectionId, MonospaceCollection>;

	constructor(manifest: SchemaManifest) {
		this.strings = manifest.strings;
		this.graph = manifest.querySchema;
		this.collections = manifest.monospaceSchema.collections;
		this.collectionsById = new Map(this.collections.map((collection) => [collection.id, collection]));
	}

	collection(id: GlobalCollectionId): MonospaceCollection | undefined {
		return this.collectionsById.get(id);
	}

	primitiveName(collectionId: GlobalCollectionId, id: GlobalPrimitiveFieldId): string | undefined {
		const field = this.collection(collectionId)?.primitiveFields.find((field) => field.id === id);
		return field && this.strings[field.apiName];
	}

	relation(collectionId: GlobalCollectionId, id: GlobalSingleRelationFieldId): { apiName: string; oppositeCollectionId: GlobalCollectionId } | undefined {
		const field = this.collection(collectionId)?.relationFields.find((field) => field.id === id);
		const apiName = field && this.strings[field.apiName];
		const oppositeCollectionId = field && this.collections[field.oppositeCollection]?.id;
		return apiName !== undefined && oppositeCollectionId !== undefined ? { apiName, oppositeCollectionId } : undefined;
	}

	/** The input object type at a bare `inputObjectTypes` index. */
	objectAt(ref: SchemaManifestInputObjectTypeRef): InputObjectType | undefined {
		return this.graph.inputObjectTypes[ref];
	}

	/**
	 * The input object type a ref points at; `undefined` when the ref names
	 * another pool (a primitive, enum, or output object). The ref's pool
	 * letter carries the kind — the pooled value is never inspected.
	 */
	objectOf(ref: SchemaManifestTypeRef): InputObjectType | undefined {
		const { pool, index } = typeRefBase(ref);
		return pool === SchemaManifestTypePools.InputObject ? this.graph.inputObjectTypes[index] : undefined;
	}

	/** An object's deduplicated field by name. `fields` interleaves [name, field] pairs. */
	field(object: InputObjectType, apiName: string): InputField | undefined {
		const index = this.fieldIndex(object, apiName);
		return index === undefined ? undefined : this.graph.inputFields[index];
	}

	/** The output object type a ref points at; `undefined` when the ref names another pool. */
	outputObjectOf(ref: SchemaManifestTypeRef): OutputObjectType | undefined {
		const { pool, index } = typeRefBase(ref);
		return pool === SchemaManifestTypePools.OutputObject ? this.graph.outputObjectTypes[index] : undefined;
	}

	/** An output object's deduplicated field by name. */
	outputField(object: OutputObjectType, apiName: string): OutputField | undefined {
		const index = this.fieldIndex(object, apiName);
		return index === undefined ? undefined : this.graph.outputFields[index];
	}

	private fieldIndex({ fields }: InputObjectType | OutputObjectType, apiName: string): number | undefined {
		for (let index = 0; index + 1 < fields.length; index += 2) {
			if (this.strings[fields[index]!] === apiName) return fields[index + 1]!;
		}

		return undefined;
	}

	/** The variant names of the enum a ref points at; empty when it names another pool. */
	enumValues(ref: SchemaManifestTypeRef): string[] {
		const { pool, index } = typeRefBase(ref);
		return pool === SchemaManifestTypePools.Enum ? (this.graph.enumTypes[index] ?? []).flatMap((name) => this.strings[name] ?? []) : [];
	}

	/** Whether an object may hold more than one of its fields at once. */
	combinesFields(object: InputObjectType): boolean {
		return !(object.constraints ?? []).some((name) => this.strings[name] === InputObjectTypeConstraints.AtMostOneField);
	}

	/** An object's field names for syntax fields such as operators. */
	fieldNames(object: InputObjectType): string[] {
		const { fields } = object;
		const names: string[] = [];

		for (let index = 0; index + 1 < fields.length; index += 2) {
			names.push(this.strings[fields[index]!] ?? '');
		}

		return names;
	}
}

/** Whether a ref's outer type admits `null`: `"p3?"` and `"[p3]?"` do, `"[p3?]"` does not. */
function isNullableTypeRef(ref: SchemaManifestTypeRef): boolean {
	return ref.endsWith('?');
}

/**
 * The base of a `"i17"` / `"[i17]"` / `"p3?"` reference: its pool letter and
 * the index within that pool.
 */
function typeRefBase(ref: SchemaManifestTypeRef): { pool: string; index: number } {
	let value: string = ref;

	for (;;) {
		if (value.endsWith('?')) value = value.slice(0, -1);
		else if (value.startsWith('[') && value.endsWith(']')) value = value.slice(1, -1);
		else break;
	}

	return { pool: value[0] ?? '', index: Number(value.slice(1)) };
}
